import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Divider,
  Flex,
  FlexItem,
  Label,
  LabelGroup,
  Spinner,
  Title,
  WizardFooterWrapper,
  useWizardContext,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationTriangleIcon, PencilAltIcon } from '@patternfly/react-icons';
import { formatPrice } from '../../../../utils/money';
import { useUserContext } from '../../../../contexts/user/UserContext';
import { useWallet } from '../../../../hooks/useWallet';
import PaymentSourceChooser from '../../../Organization/Billing/PaymentSourceChooser';
import type { PaymentSource } from '../../../../types/models/Payment';
import { useImpersonation } from '../../../../contexts/impersonation/ImpersonationContext';
import { STEP_IDS, cleanNameservers, vatRateLabel } from './types';
import type { ContactDraft, NsMode } from './types';
import type { DnsEndpoint, DomainAvailabilityResult, DomainOrderPreview } from '../../../../types/models';

interface RecapStepProps {
  cart: DomainAvailabilityResult[];
  contact: ContactDraft;
  nsMode: NsMode;
  endpoint: DnsEndpoint | null;
  hostnames: string[];
  preview: DomainOrderPreview | null;
  previewLoading: boolean;
  previewError: string | null;
  /** Triggers the exact-pricing preview for the given cart FQDNs. */
  onRequestPreview: (domains: string[]) => void;
  agreementsAccepted: boolean;
  onAgreementsChange: (checked: boolean) => void;
  /** Payment source (prepaid balance / saved card / hosted checkout) — lifted to the wizard, sent as `payment`. */
  paymentSource: PaymentSource;
  onPaymentSourceChange: (source: PaymentSource) => void;
  /** Checkout only: also save the card used at Mollie for later one-click payments. */
  saveCard: boolean;
  onSaveCardChange: (checked: boolean) => void;
}

const SectionHeader: React.FunctionComponent<{ title: string; onEdit: () => void; editLabel: string }> = ({
  title,
  onEdit,
  editLabel,
}) => (
  <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
    <FlexItem>
      <Title headingLevel="h3" size="md">
        {title}
      </Title>
    </FlexItem>
    <FlexItem>
      <Button variant="link" isInline icon={<PencilAltIcon />} onClick={onEdit} aria-label={editLabel}>
        Edit
      </Button>
    </FlexItem>
  </Flex>
);

/**
 * Forewarns an Ancalagon staff user (the REAL authenticated user — the backend waives on that even
 * while impersonating a customer, so this deliberately ignores `isImpersonating`) that confirming
 * will NOT go through Mollie: the order is created as already paid and an unpaid invoice is issued
 * to the tenant. `is_ancalagon_staff` is a UI hint only; the backend decides authoritatively.
 * Renders nothing for non-staff. Shared by the registration and transfer recap steps.
 */
export const StaffPaymentWaivedAlert: React.FunctionComponent = () => {
  const user = useUserContext();
  const { target, isImpersonating } = useImpersonation();

  if (user?.is_ancalagon_staff !== true) {
    return null;
  }

  return (
    <Alert variant="info" isInline title="No payment — staff order" className="pf-v6-u-mt-sm">
      As Ancalagon staff you won&apos;t be redirected to the payment page: the order is created as already paid and
      fulfilment starts automatically.{' '}
      {isImpersonating && target ? (
        <>
          An unpaid invoice will be issued to <strong>{target.name}</strong>, to settle later through the normal invoice
          flow.
        </>
      ) : (
        <>An unpaid invoice will be issued to your own organization (the Ancalagon house tenant is never invoiced).</>
      )}
    </Alert>
  );
};

const RecapStep: React.FunctionComponent<RecapStepProps> = ({
  cart,
  contact,
  nsMode,
  endpoint,
  hostnames,
  preview,
  previewLoading,
  previewError,
  onRequestPreview,
  agreementsAccepted,
  onAgreementsChange,
  paymentSource,
  onPaymentSourceChange,
  saveCard,
  onSaveCardChange,
}) => {
  const { goToStepById } = useWizardContext();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  // The wallet (balance + default saved card) is fetched here, on recap entry — like the preview —
  // never for a staff user, whose payment the backend waives (the `payment` object is ignored).
  const { wallet, defaultPaymentMethod, loading: walletLoading, error: walletError } = useWallet(!isStaff);
  const cleanedHostnames = cleanNameservers(hostnames);
  const unsupported = preview?.items.filter((i) => !i.supported) ?? [];

  // This step body only mounts when the recap step is active (PatternFly unmounts inactive step
  // bodies), so requesting the preview here fetches exactly on entry — and re-fetches whenever the
  // cart changed since (the user edited domains and came back), without relying on onStepChange
  // (which goToStepById deliberately doesn't fire).
  const cartDomains = useMemo(() => cart.map((c) => c.domain), [cart]);
  useEffect(() => {
    if (cartDomains.length > 0) {
      onRequestPreview(cartDomains);
    }
  }, [cartDomains, onRequestPreview]);

  // Prefer the preview's per-item HT prices (the authoritative, client-specific values); fall back to
  // the live cart while the preview is (re)loading, so a just-removed domain never flashes back in.
  const rows =
    preview !== null && !previewLoading
      ? preview.items.map((i) => ({ domain: i.domain, tld: i.tld, price: i.unit_price_ht, supported: i.supported }))
      : cart.map((c) => ({ domain: c.domain, tld: c.tld, price: c.price, supported: c.supported }));

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsLg' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Review &amp; pay
        </Title>
        <Content component="p">Review your order below. You can still go back and edit any step until you pay.</Content>
      </FlexItem>

      <FlexItem>
        <SectionHeader title="Domains" onEdit={() => goToStepById(STEP_IDS.search)} editLabel="Edit domains" />
        <Table aria-label="Order domains" variant="compact" className="pf-v6-u-mt-sm">
          <Thead>
            <Tr>
              <Th>Domain</Th>
              <Th>TLD</Th>
              <Th>Price (excl. VAT)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.domain}>
                <Td dataLabel="Domain">{r.domain}</Td>
                <Td dataLabel="TLD">.{r.tld}</Td>
                <Td dataLabel="Price (excl. VAT)">
                  {r.supported && r.price !== null ? (
                    formatPrice(r.price)
                  ) : (
                    <Label color="orange" icon={<ExclamationTriangleIcon />}>
                      Not available
                    </Label>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {unsupported.length > 0 && (
          <Alert variant="warning" isInline title="Some domains can't be ordered" className="pf-v6-u-mt-sm">
            {unsupported.map((i) => i.domain).join(', ')} {unsupported.length === 1 ? 'is' : 'are'} no longer offered
            and will be excluded. Go back to remove {unsupported.length === 1 ? 'it' : 'them'} before paying.
          </Alert>
        )}
      </FlexItem>

      <Divider />

      <FlexItem>
        <SectionHeader
          title="Registrant contact"
          onEdit={() => goToStepById(STEP_IDS.contact)}
          editLabel="Edit contact"
        />
        <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm">
          <DescriptionListGroup>
            <DescriptionListTerm>Name</DescriptionListTerm>
            <DescriptionListDescription>
              {contact.first_name} {contact.last_name}
              {contact.organization ? ` — ${contact.organization}` : ''}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Email</DescriptionListTerm>
            <DescriptionListDescription>{contact.email}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Phone</DescriptionListTerm>
            <DescriptionListDescription>{contact.phone}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Address</DescriptionListTerm>
            <DescriptionListDescription>
              {contact.address_line1}
              {contact.address_line2 ? `, ${contact.address_line2}` : ''}, {contact.zipcode} {contact.city},{' '}
              {contact.state}, {contact.countrycode}
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </FlexItem>

      <Divider />

      <FlexItem>
        <SectionHeader
          title="Nameservers"
          onEdit={() => goToStepById(STEP_IDS.nameservers)}
          editLabel="Edit nameservers"
        />
        <div className="pf-v6-u-mt-sm">
          {nsMode === 'endpoint' && endpoint ? (
            <>
              <Content component="p">
                Managed endpoint: <strong>{endpoint.name}</strong>
              </Content>
              {endpoint.public_nameservers.length > 0 && (
                <LabelGroup numLabels={10} aria-label="Endpoint nameservers">
                  {endpoint.public_nameservers.map((ns) => (
                    <Label key={ns} isCompact>
                      {ns}
                    </Label>
                  ))}
                </LabelGroup>
              )}
            </>
          ) : (
            <LabelGroup numLabels={10} aria-label="Nameservers">
              {cleanedHostnames.map((ns) => (
                <Label key={ns} isCompact>
                  {ns}
                </Label>
              ))}
            </LabelGroup>
          )}
        </div>
      </FlexItem>

      <Divider />

      <FlexItem>
        <Title headingLevel="h3" size="md">
          Total
        </Title>
        {previewLoading ? (
          <Bullseye>
            <Spinner size="lg" aria-label="Computing the total" />
          </Bullseye>
        ) : previewError ? (
          <Alert variant="warning" isInline title="Couldn't compute the exact total" className="pf-v6-u-mt-sm">
            {previewError} The final amount will be shown at checkout.
          </Alert>
        ) : preview ? (
          <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm">
            <DescriptionListGroup>
              <DescriptionListTerm>Subtotal (excl. VAT)</DescriptionListTerm>
              <DescriptionListDescription>{formatPrice(preview.amount_ht)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>VAT</DescriptionListTerm>
              <DescriptionListDescription>{vatRateLabel(preview.vat_rate)}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>
                <strong>{isStaff ? 'Invoice total' : 'Total to pay'}</strong>
              </DescriptionListTerm>
              <DescriptionListDescription>
                <Content component="p">
                  <strong>{formatPrice(preview.amount_ttc)}</strong>
                </Content>
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        ) : null}
        <StaffPaymentWaivedAlert />
      </FlexItem>

      {!isStaff && (
        <>
          <Divider />
          <FlexItem>
            <Title headingLevel="h3" size="md" className="pf-v6-u-mb-sm">
              Payment
            </Title>
            <PaymentSourceChooser
              idPrefix="reg"
              value={paymentSource}
              onChange={onPaymentSourceChange}
              saveCard={saveCard}
              onSaveCardChange={onSaveCardChange}
              amountDue={preview && !previewLoading ? preview.amount_ttc : null}
              wallet={wallet}
              defaultPaymentMethod={defaultPaymentMethod}
              walletLoading={walletLoading}
              walletError={walletError}
            />
          </FlexItem>
        </>
      )}

      <Divider />

      <FlexItem>
        <Checkbox
          id="reg-agreements"
          isChecked={agreementsAccepted}
          onChange={(_event, checked) => onAgreementsChange(checked)}
          label="I accept the registrar agreements and the domain registration terms."
          isRequired
        />
      </FlexItem>
    </Flex>
  );
};

export const RecapFooter: React.FunctionComponent<{
  canSubmit: boolean;
  submitting: boolean;
  onConfirm: () => Promise<{ jumpToStepId?: string }>;
}> = ({ canSubmit, submitting, onConfirm }) => {
  const { goToPrevStep, goToStepById, close } = useWizardContext();
  // Staff orders skip the Mollie payment entirely (see StaffPaymentWaivedAlert), so promising
  // "& pay" would be wrong. Gate on the REAL user's staff flag alone — it applies while impersonating.
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;

  const handleConfirm = async () => {
    const { jumpToStepId } = await onConfirm();
    if (jumpToStepId) {
      goToStepById(jumpToStepId);
    }
  };

  return (
    <WizardFooterWrapper>
      <Button variant="secondary" onClick={() => goToPrevStep()} isDisabled={submitting}>
        Back
      </Button>
      <Button variant="primary" onClick={handleConfirm} isDisabled={!canSubmit || submitting} isLoading={submitting}>
        {isStaff ? 'Confirm order' : 'Confirm & pay'}
      </Button>
      <Button variant="link" onClick={() => close()} isDisabled={submitting}>
        Cancel
      </Button>
    </WizardFooterWrapper>
  );
};

export default RecapStep;
